import React, { useState, useMemo, useCallback } from 'react';
import { X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const formatStation = (station) => {
  if (station === null || station === undefined) return 'N/A';
  const hundreds = Math.floor(station / 100);
  const remainder = station % 100;
  return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
};

const SortHeader = ({ label, sortKey, currentSort, onSort, className = '' }) => {
  const isActive = currentSort.key === sortKey;
  const Icon = isActive ? (currentSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors ${className}`}
    >
      {label}
      <Icon className={`h-3 w-3 ${isActive ? 'text-gray-700' : 'text-gray-400'}`} />
    </button>
  );
};

export default function DataTableDrawer({
  segments = [],
  events = [],
  iriColorScale,
  legendIriFilter,
  onNavigateSegment,
  onNavigateEvent,
  onClose,
  isReversed = false,
}) {
  const [activeTab, setActiveTab] = useState('segments');
  const [sort, setSort] = useState({ key: 'from_sta', dir: 'asc' });

  const handleSort = useCallback((key) => {
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const ranges = iriColorScale?.ranges || [];

  const getIRIRating = useCallback((iri) => {
    for (const range of ranges) {
      if (iri <= range.max) return range.label;
    }
    return 'Unknown';
  }, [ranges]);

  const getIRIColor = useCallback((iri) => {
    for (const range of ranges) {
      if (iri <= range.max) return range.color;
    }
    return iriColorScale?.defaultColor || '#A0A0A0';
  }, [ranges, iriColorScale]);

  // Count ALRs per segment
  const alrCountMap = useMemo(() => {
    const map = new Map();
    (events || []).forEach(e => {
      if ((e.event_type || '').toUpperCase() !== 'LR') return;
      // Count for each segment whose station range contains this event
      (segments || []).forEach(seg => {
        if (e.from_station_ft >= seg.from_station_ft && e.to_station_ft <= seg.to_station_ft) {
          map.set(seg.segment_id, (map.get(seg.segment_id) || 0) + 1);
        }
      });
    });
    return map;
  }, [events, segments]);

  // Build cumulative distance map (station-order, before filtering/sorting)
  const cumulativeDistMap = useMemo(() => {
    const map = new Map();
    const sorted = [...(segments || [])].sort((a, b) =>
      isReversed
        ? (b.from_station_ft || 0) - (a.from_station_ft || 0)
        : (a.from_station_ft || 0) - (b.from_station_ft || 0)
    );
    let cumDist = 0;
    sorted.forEach(seg => {
      const width = seg.distance_ft || Math.abs((seg.to_station_ft || 0) - (seg.from_station_ft || 0));
      map.set(seg.segment_id, { startDist: cumDist, stopDist: cumDist + width, width });
      cumDist += width;
    });
    return map;
  }, [segments, isReversed]);

  // Filter and sort segments
  const filteredSegments = useMemo(() => {
    let segs = (segments || []).filter(s => {
      if (legendIriFilter === null) return !s.exception;
      if (legendIriFilter === 'exception') return s.exception;
      if (s.exception) return false;
      const iri = s.iri_avg_in_per_mi || s.iri_in_per_mi || 0;
      const prevMax = legendIriFilter === 0 ? 0 : ranges[legendIriFilter - 1]?.max || 0;
      return iri > prevMax && iri <= ranges[legendIriFilter]?.max;
    });

    const m = sort.dir === 'asc' ? 1 : -1;
    segs.sort((a, b) => {
      const distA = cumulativeDistMap.get(a.segment_id);
      const distB = cumulativeDistMap.get(b.segment_id);
      switch (sort.key) {
        case 'from_sta': return m * ((distA?.startDist || 0) - (distB?.startDist || 0));
        case 'to_sta': return m * ((distA?.stopDist || 0) - (distB?.stopDist || 0));
        case 'length': return m * ((distA?.width || 0) - (distB?.width || 0));
        case 'left_iri': return m * ((a.iri_left_in_per_mi || 0) - (b.iri_left_in_per_mi || 0));
        case 'right_iri': return m * ((a.iri_right_in_per_mi || 0) - (b.iri_right_in_per_mi || 0));
        case 'mri': return m * (((a.iri_avg_in_per_mi || a.iri_in_per_mi || 0) - (b.iri_avg_in_per_mi || b.iri_in_per_mi || 0)));
        case 'alrs': return m * ((alrCountMap.get(a.segment_id) || 0) - (alrCountMap.get(b.segment_id) || 0));
        case 'speed': return m * ((a.avg_speed_mph || 0) - (b.avg_speed_mph || 0));
        default: return 0;
      }
    });
    return segs;
  }, [segments, legendIriFilter, ranges, sort, alrCountMap, cumulativeDistMap]);

  // All bump/dip events for counting per ALR
  const allBumpDips = useMemo(() => {
    return (events || []).filter(e => {
      const t = (e.event_type || '').toUpperCase();
      return t === 'BUMP' || t === 'DIP';
    });
  }, [events]);

  // Filter events by type
  const alrs = useMemo(() => {
    const m = sort.dir === 'asc' ? 1 : -1;
    return (events || [])
      .filter(e => (e.event_type || '').toUpperCase() === 'LR')
      .map(e => {
        const from = e.from_station_ft || 0;
        const to = e.to_station_ft || 0;
        const wp = e.wheelpath;
        let bumpCount = 0;
        let dipCount = 0;
        allBumpDips.forEach(bd => {
          const bdFrom = bd.from_station_ft || 0;
          const inRange = bdFrom >= from && bdFrom <= to;
          const wpMatch = !wp || bd.wheelpath === wp;
          if (inRange && wpMatch) {
            if ((bd.event_type || '').toUpperCase() === 'BUMP') bumpCount++;
            else dipCount++;
          }
        });
        return { ...e, bumpCount, dipCount, length_ft: Math.abs(to - from) };
      })
      .sort((a, b) => {
        if (sort.key === 'magnitude') return m * ((a.magnitude_in || 0) - (b.magnitude_in || 0));
        return m * ((a.from_station_ft || 0) - (b.from_station_ft || 0));
      });
  }, [events, sort, allBumpDips]);

  const bumpsDips = useMemo(() => {
    const m = sort.dir === 'asc' ? 1 : -1;
    return (events || [])
      .filter(e => {
        const t = (e.event_type || '').toUpperCase();
        return t === 'BUMP' || t === 'DIP';
      })
      .sort((a, b) => {
        if (sort.key === 'magnitude') return m * ((a.magnitude_in || 0) - (b.magnitude_in || 0));
        if (sort.key === 'type') return m * ((a.event_type || '').localeCompare(b.event_type || ''));
        return m * ((a.from_station_ft || 0) - (b.from_station_ft || 0));
      });
  }, [events, sort]);

  const tabs = [
    { id: 'segments', label: 'Segments', count: filteredSegments.length },
    { id: 'alrs', label: 'ALRs', count: alrs.length },
    { id: 'bumps_dips', label: 'Bumps & Dips', count: bumpsDips.length },
  ];

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setSort({ key: 'from_sta', dir: 'asc' });
  }, []);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                activeTab === tab.id
                  ? 'bg-red-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 ${activeTab === tab.id ? 'text-red-200' : 'text-gray-400'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 transition-colors">
          <X size={16} />
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
            {activeTab === 'segments' && (
              <tr>
                <th className="px-3 py-2 text-left w-6"></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Start Dist (ft)" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Stop Dist (ft)" sortKey="to_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Length (ft)" sortKey="length" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Start Station" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Stop Station" sortKey="to_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Left IRI" sortKey="left_iri" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Right IRI" sortKey="right_iri" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="MRI" sortKey="mri" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Scale" sortKey="mri" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="ALRs" sortKey="alrs" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Avg Speed" sortKey="speed" currentSort={sort} onSort={handleSort} /></th>
              </tr>
            )}
            {activeTab === 'alrs' && (
              <tr>
                <th className="px-3 py-2 text-left"><SortHeader label="Start Station" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Stop Station" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Peak Station" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Peak IRI" sortKey="magnitude" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Length" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left">Wheelpath</th>
                <th className="px-3 py-2 text-left"><SortHeader label="Bumps" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Dips" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
              </tr>
            )}
            {activeTab === 'bumps_dips' && (
              <tr>
                <th className="px-3 py-2 text-left"><SortHeader label="Type" sortKey="type" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Station" sortKey="from_sta" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left"><SortHeader label="Magnitude" sortKey="magnitude" currentSort={sort} onSort={handleSort} /></th>
                <th className="px-3 py-2 text-left">Wheelpath</th>
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {activeTab === 'segments' && filteredSegments.map((seg) => {
              const iri = seg.iri_avg_in_per_mi || seg.iri_in_per_mi || 0;
              const leftIri = seg.iri_left_in_per_mi || 0;
              const rightIri = seg.iri_right_in_per_mi || 0;
              const dist = cumulativeDistMap.get(seg.segment_id) || {};
              const startStaLabel = formatStation(isReversed ? seg.to_station_ft : seg.from_station_ft);
              const stopStaLabel = formatStation(isReversed ? seg.from_station_ft : seg.to_station_ft);
              const alrCount = alrCountMap.get(seg.segment_id) || 0;
              return (
                <tr
                  key={seg.segment_id}
                  onClick={() => onNavigateSegment(seg)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.exception ? iriColorScale?.exceptionColor : getIRIColor(iri) }} />
                  </td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-600">{(dist.startDist || 0).toFixed(1)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-600">{(dist.stopDist || 0).toFixed(1)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-600">{(dist.width || 0).toFixed(1)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{startStaLabel}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{stopStaLabel}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{leftIri.toFixed(1)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{rightIri.toFixed(1)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums font-semibold text-gray-800">{iri.toFixed(1)}</td>
                  <td className="px-3 py-1.5 text-gray-600">{seg.exception ? 'Exception' : getIRIRating(iri)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums">{alrCount > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{alrCount}</span> : <span className="text-gray-400">0</span>}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-600">{seg.avg_speed_mph ? seg.avg_speed_mph.toFixed(1) : '—'}</td>
                </tr>
              );
            })}

            {activeTab === 'alrs' && alrs.map((e) => {
              const startSta = isReversed ? e.to_station_ft : e.from_station_ft;
              const stopSta = isReversed ? e.from_station_ft : e.to_station_ft;
              return (
                <tr
                  key={e.event_id}
                  onClick={() => onNavigateEvent(e, 'LR')}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{formatStation(startSta)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{formatStation(stopSta)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{formatStation(e.peak_station_ft)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums font-semibold text-gray-800">{(e.magnitude_in || 0).toFixed(2)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-600">{(e.length_ft || 0).toFixed(1)} ft</td>
                  <td className="px-3 py-1.5 text-gray-600">{e.wheelpath || '—'}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums">{e.bumpCount > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{e.bumpCount}</span> : <span className="text-gray-400">0</span>}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums">{e.dipCount > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">{e.dipCount}</span> : <span className="text-gray-400">0</span>}</td>
                </tr>
              );
            })}

            {activeTab === 'bumps_dips' && bumpsDips.map((e) => {
              const isBump = (e.event_type || '').toUpperCase() === 'BUMP';
              return (
                <tr
                  key={e.event_id}
                  onClick={() => onNavigateEvent(e, (e.event_type || '').toUpperCase())}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-1.5">
                    <span className="text-xs font-semibold text-gray-600">
                      {isBump ? 'Bump' : 'Dip'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-700">{formatStation(e.from_station_ft)}</td>
                  <td className="px-3 py-1.5 font-mono tabular-nums font-semibold text-gray-800">{(e.magnitude_in || 0).toFixed(2)} in</td>
                  <td className="px-3 py-1.5 text-gray-600">{e.wheelpath || '—'}</td>
                </tr>
              );
            })}

            {((activeTab === 'segments' && filteredSegments.length === 0) ||
              (activeTab === 'alrs' && alrs.length === 0) ||
              (activeTab === 'bumps_dips' && bumpsDips.length === 0)) && (
              <tr>
                <td colSpan={12} className="px-4 py-8 text-center text-gray-400">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
